import { Action, createReducer, on } from '@ngrx/store';
import { CrudState, CrudStateObject } from '@wdx/clmi/api-services/models';
import { Build } from '@wdx/clmi/api-services/services';
import {
    AppLanguage,
    QuickCreateEntry,
    Version,
} from '@wdx/clmi/clmi-swagger-gen';
import { GLOBAL_STATE_INDEX_ID } from '@wdx/shared/utils';
import * as systemActions from './system.actions';

export interface State {
    version: CrudStateObject<Version>;
    build: CrudStateObject<Build>;
    appLanguages: CrudState<AppLanguage>;
    quickCreateEntries: CrudState<QuickCreateEntry>;
}

export const initialState: State = {
    version: {},
    build: {},
    appLanguages: {},
    quickCreateEntries: {},
};

const reducerSetup = createReducer(
    initialState,

    on(
        systemActions.getVersion,
        (state): State => ({
            ...state,
            version: {
                ...state.version,
                [GLOBAL_STATE_INDEX_ID]: {
                    ...(state.version[GLOBAL_STATE_INDEX_ID] ||
                        ({} as CrudState<Version>)),
                    isLoadingSingle: true,
                    hasLoadingSingleError: false,
                },
            },
        }),
    ),

    on(
        systemActions.getVersionSuccess,
        (state, props): State => ({
            ...state,
            version: {
                ...state.version,
                [GLOBAL_STATE_INDEX_ID]: {
                    ...state.version[GLOBAL_STATE_INDEX_ID],
                    isLoadingSingle: false,
                    hasLoadingSingleError: false,
                    single: props.version,
                },
            },
        }),
    ),

    on(
        systemActions.getVersionFailure,
        (state): State => ({
            ...state,
            version: {
                ...state.version,
                [GLOBAL_STATE_INDEX_ID]: {
                    ...state.version[GLOBAL_STATE_INDEX_ID],
                    isLoadingSingle: false,
                    hasLoadingSingleError: true,
                },
            },
        }),
    ),

    on(
        systemActions.getBuild,
        (state): State => ({
            ...state,
            build: {
                ...state.build,
                [GLOBAL_STATE_INDEX_ID]: {
                    ...(state.build[GLOBAL_STATE_INDEX_ID] ||
                        ({} as CrudState<Build>)),
                    isLoadingSingle: true,
                    hasLoadingSingleError: false,
                },
            },
        }),
    ),

    on(
        systemActions.getBuildSuccess,
        (state, props): State => ({
            ...state,
            build: {
                ...state.build,
                [GLOBAL_STATE_INDEX_ID]: {
                    ...state.build[GLOBAL_STATE_INDEX_ID],
                    isLoadingSingle: false,
                    hasLoadingSingleError: false,
                    single: props.build,
                },
            },
        }),
    ),

    on(
        systemActions.getBuildFailure,
        (state): State => ({
            ...state,
            build: {
                ...state.build,
                [GLOBAL_STATE_INDEX_ID]: {
                    ...state.build[GLOBAL_STATE_INDEX_ID],
                    isLoadingSingle: false,
                    hasLoadingSingleError: true,
                },
            },
        }),
    ),

    on(
        systemActions.getAppLanguages,
        (state): State => ({
            ...state,
            appLanguages: {
                ...state.appLanguages,
                isLoadingList: true,
                hasLoadingListError: false,
            },
        }),
    ),

    on(
        systemActions.getAppLanguagesSuccess,
        (state, props): State => ({
            ...state,
            appLanguages: {
                ...state.appLanguages,
                isLoadingList: false,
                hasLoadingListError: false,
                list: props.applicationLanguages,
            },
        }),
    ),

    on(
        systemActions.getAppLanguagesFailure,
        (state): State => ({
            ...state,
            appLanguages: {
                ...state.appLanguages,
                isLoadingList: false,
                hasLoadingListError: true,
            },
        }),
    ),

    on(
        systemActions.getQuickCreate,
        (state): State => ({
            ...state,
            quickCreateEntries: {
                ...state.quickCreateEntries,
                isLoadingList: true,
                hasLoadingListError: false,
            },
        }),
    ),

    on(
        systemActions.getQuickCreateSuccess,
        (state, props): State => ({
            ...state,
            quickCreateEntries: {
                ...state.quickCreateEntries,
                list: props.entries,
                isLoadingList: false,
                hasLoadingListError: false,
            },
        }),
    ),

    on(
        systemActions.getQuickCreateFailure,
        (state): State => ({
            ...state,
            quickCreateEntries: {
                ...state.quickCreateEntries,
                isLoadingList: false,
                hasLoadingListError: true,
            },
        }),
    ),
);

export function reducer(state: State | undefined, action: Action) {
    return reducerSetup(state, action);
}
